import { useCallback, useEffect, useRef, useState } from 'react';
import { useWordData } from 'src/hook/data/useWordData';
import { useWords } from 'src/hook/data/useWords';
import { Challenge } from 'src/model/Challenge';
import { CardType } from 'src/model/enum/enums';
import { WordData } from 'src/model/WordData';
import { useChallenge } from '../../hook/data/useChallenge';
import { useLearn } from '../../hook/data/useLearn';
import { useLearning } from '../../hook/data/useLearning';
import { Word } from '../../model/Word';
import RenderChallenge from '../Challenges/RenderChallenge';
import Spinner from '../ui/Features/Spinner/Spinner';
import EmptyLearn from './MainLearn/EmptyLearn/EmptyLearn';
import MainLearn from './MainLearn/MainLearn';

const Learn = () => {
    const { learningInfo } = useLearning();
    const { cards, isFinished, currentCard, currentCardIndex, initializeSequence, completeCurrentCard } = useLearn();
    const [currentWord, setCurrentWord] = useState<Word | undefined>(undefined);
    const { getWordById } = useWords();
    const { getChallenge } = useChallenge();
    const [currentChallenge, setCurrentChallenge] = useState<Challenge | undefined>(undefined);
    const [isEmptyLearn, setIsEmptyLearn] = useState(false);
    const isInitialized = useRef(false);
    const { fetchWordData } = useWordData();
    const [currentWordData, setCurrentWordData] = useState<WordData | undefined>(undefined);
    const [shuffledChoices, setShuffledChoices] = useState<{ isCorrect: boolean; text: string; wordId: number }[]>([]);

    const fetchChallengeData = useCallback(async (wordId: number) => {
        const result = await getChallenge(wordId);
        if (!result) return;
        setCurrentChallenge(result.challenge);

        const allChoices = [
            { isCorrect: true, text: result.challenge.Answer, wordId: result.wordId },
            ...result.challenge.Wrong.map(w => ({
                isCorrect: false,
                text: w.Text, wordId: w?.Word ?? 0
            }))
        ];

        const shuffled = [...allChoices].sort(() => Math.random() - 0.5);
        setShuffledChoices(allChoices.length > 2 ? shuffled : allChoices);
    }, [getChallenge]);

    const fetchData = useCallback(async (wordId: number) => {
        const wordData = await fetchWordData(wordId);
        if (!wordData) return;
        setCurrentWordData(wordData);

    }, [fetchWordData]);

    // Initialize sequence only once
    useEffect(() => {
        if (currentCard) return;
        if (isInitialized.current) return;

        if (learningInfo?.ToLearn?.length && learningInfo?.ToLearn?.length > 0) {
            initializeSequence(learningInfo.ToLearn);
            isInitialized.current = true;
        } else {
            setIsEmptyLearn(true);
        }
    }, [learningInfo?.ToLearn, initializeSequence, currentCard]);



    useEffect(() => {
        if (isFinished) {
            setIsEmptyLearn(true);
            return;
        }
        if (!currentCard) return;

        setCurrentWord(getWordById(currentCard.wordId!));

        if (currentCard.type === CardType.Challenge) {
            fetchChallengeData(currentCard.wordId!);
        }
        else if (currentCard.type === CardType.Learn) {
            fetchData(currentCard.wordId!);
        }
    }, [currentCard, getWordById, fetchChallengeData,
        fetchData, isFinished]);

    const handleAnswer = (isCorrect: boolean) => {
        completeCurrentCard();
    };

    if (isEmptyLearn) return <EmptyLearn />;
    if (!currentWord) return <Spinner fullPage />;

    if (currentCard?.type === CardType.Learn) {
        return (
            <MainLearn
                word={currentWord}
                wordData={currentWordData!}
            />
        );
    } else if (currentCard?.type === CardType.Challenge) {
        return (
            <RenderChallenge
                currentChallenge={currentChallenge}
                correctWord={currentWord}
                shuffledChoices={shuffledChoices}
                onAnswer={handleAnswer}
                showFinish={false}
            />
        );
    }

    return null;
};

export default Learn;